import { Bell, Info, CheckCircle, AlertTriangle, AlertCircle, CheckCheck, X } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/common/Badge';
import { useNotifications } from '@/hooks/useNotifications';
import type { NotificationItem } from '@/services/notificationService';

const TYPE_STYLE: Record<NotificationItem['type'], { icon: any; tone: string }> = {
  info:    { icon: Info,          tone: 'text-primary bg-primary/10' },
  success: { icon: CheckCircle,   tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20' },
  warning: { icon: AlertTriangle, tone: 'text-amber-600 bg-amber-50 dark:bg-amber-950/20' },
  error:   { icon: AlertCircle,   tone: 'text-red-500 bg-red-50 dark:bg-red-950/20' },
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const { items, unreadCount, refresh, markRead, markAllRead, dismiss } = useNotifications();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen((o) => !o);
    if (!open) refresh();
  };

  const openItem = async (n: NotificationItem) => {
    setOpen(false);
    if (!n.isRead) await markRead(n.id);
    if (n.href) navigate(n.href);
  };

  const onDismiss = async (e: React.MouseEvent, n: NotificationItem) => {
    e.stopPropagation();
    await dismiss(n.id);
  };

  const timeAgo = (iso: string) => {
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="relative">
      <button onClick={handleOpen} className="relative p-2 hover:bg-accent rounded-md" aria-label="Notifications">
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-20 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="text-sm font-semibold">Notifications</p>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary hover:underline">
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
                  <CheckCheck className="h-7 w-7 opacity-40" />
                  <p className="text-sm">You're all caught up</p>
                </div>
              ) : (
                items.map((n) => {
                  const { icon: Icon, tone } = TYPE_STYLE[n.type] || TYPE_STYLE.info;
                  return (
                    <div
                      key={n.id}
                      onClick={() => openItem(n)}
                      className={`group w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/50 last:border-0 hover:bg-accent transition-colors cursor-pointer ${
                        n.isRead ? 'opacity-60' : ''
                      }`}
                    >
                      <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${tone}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {!n.isRead && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                          <p className="text-sm font-medium truncate">{n.title}</p>
                        </div>
                        {n.description && <p className="text-xs text-muted-foreground truncate">{n.description}</p>}
                        <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.createdAt)}</p>
                      </div>
                      <button
                        onClick={(e) => onDismiss(e, n)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0"
                        aria-label="Dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full px-4 py-2.5 text-xs font-medium text-primary hover:bg-accent border-t border-border transition-colors"
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );
}
